import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  Theme,
  Tooltip
} from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import React, { ChangeEvent } from "react";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%"
    }
  })
);

type SelectProps<TValue> = {
  options: TValue[];
  label: string;
  inputValue?: string;
  name?: string;
  value: TValue | null;
  error?: boolean;
  helperText?: React.ReactNode;
  loading?: boolean;
  disabled?: AutocompleteProps<TValue, false, false, false>["disabled"];
  onBlur?: React.FocusEventHandler;
  onOpen?: AutocompleteProps<TValue, false, false, false>["onOpen"];
  onClose?: AutocompleteProps<TValue, false, false, false>["onClose"];
  onChange: (event: ChangeEvent<{}>, value: TValue | null) => void;
  onInputChange?: (event: ChangeEvent<{}>, inputValue: string) => void;
  getOptionLabel?: (option: TValue) => string;
  renderOption?: (option: TValue) => React.ReactNode;
  getOptionTooltip?: (option: TValue) => React.ReactNode;
  inputStyle?: React.CSSProperties;
  id: string;
};

export default function Select<TValue>(props: SelectProps<TValue>) {
  const jss = useStyles();
  return (
    <Autocomplete
      loading={props.loading}
      className={jss.root}
      getOptionLabel={props.getOptionLabel}
      renderOption={
        props.renderOption !== undefined || props.getOptionTooltip !== undefined
          ? (optionProps, value) => {
              const content =
                props.renderOption?.(value) ??
                props.getOptionLabel?.(value) ??
                String(value);
              const tooltip = props.getOptionTooltip?.(value);
              return (
                <li {...optionProps}>
                  {tooltip ? (
                    <Tooltip title={tooltip} placement="right" arrow>
                      <span style={{ display: "block", width: "100%" }}>
                        {content}
                      </span>
                    </Tooltip>
                  ) : (
                    content
                  )}
                </li>
              );
            }
          : undefined
      }
      options={props.options}
      disabled={props.disabled}
      onOpen={props.onOpen}
      onClose={props.onClose}
      renderInput={params => (
        <TextField
          {...params}
          name={props.name}
          helperText={props.helperText}
          error={props.error}
          label={props.label}
          variant="outlined"
          fullWidth
          inputProps={{
            ...params.inputProps,
            style: {
              ...params.inputProps.style,
              ...props.inputStyle
            }
          }}
        />
      )}
      onBlur={props.onBlur}
      onChange={props.onChange}
      value={props.value}
      inputValue={props.inputValue}
      onInputChange={props.onInputChange}
      id={props.id}
    />
  );
}
